import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import { FC, KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { COLORS } from 'services/constants/colors';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

export type TPpwrEditValueField = {
	label: string;
	// Value of the current row shown when the row is selected.
	initialValue: string;
	isValid: (value: string) => boolean;
	invalidText: string;
};

type TPpwrEditValueDialogProps = {
	// Number of rows the dialog can step through.
	rowsCount: number;
	// Index of the row being edited, `null` when the dialog is closed.
	index: number | null;
	setIndex: (index: number) => void;
	onClose: () => void;
	title: ReactNode;
	fields: TPpwrEditValueField[];
	// Called with all values (same order as `fields`) when every value is valid
	// and at least one differs from its `initialValue`.
	onSave: (values: string[]) => void;
};

// Edits values of a table row. Every button except Cancel saves the values;
// Enter moves to the next field, and on the last field saves and moves to the next row.
export const PpwrEditValueDialog: FC<TPpwrEditValueDialogProps> = ({
	rowsCount,
	index,
	setIndex,
	onClose,
	title,
	fields,
	onSave,
}) => {
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const [values, setValues] = useState<string[]>([]);

	// Load the values of the newly selected row and select the first one, so the
	// user can type numbers and press Enter field after field, row after row.
	useEffect(() => {
		if (index === null) return;
		setValues(fields.map((field) => field.initialValue));
		requestAnimationFrame(() => inputRefs.current[0]?.select());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [index]);

	const fieldIsValid = fields.map((field, i) => field.isValid(values[i] ?? ''));
	const valuesAreValid = fieldIsValid.every(Boolean);
	const hasPrevious = index !== null && index > 0;
	const hasNext = index !== null && index < rowsCount - 1;

	const handleSave = () => {
		if (!valuesAreValid) return;
		const trimmed = fields.map((_, i) => (values[i] ?? '').trim());
		const changed = fields.some((field, i) => trimmed[i] !== field.initialValue.trim());
		if (changed) onSave(trimmed);
	};

	const handleButtonSave = () => {
		handleSave();
		onClose();
	};

	const goPrevious = () => {
		handleSave();
		if (hasPrevious) setIndex(index - 1);
	};

	const goNext = () => {
		handleSave();
		if (hasNext) setIndex(index + 1);
		else onClose();
	};

	const handleKeyDown = (fieldIndex: number) => (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter') return;
		event.preventDefault();
		if (fieldIndex < fields.length - 1) inputRefs.current[fieldIndex + 1]?.select();
		else goNext();
	};

	const handleChange = (fieldIndex: number, value: string) =>
		setValues((prev) => prev.map((item, i) => (i === fieldIndex ? value : item)));

	return (
		<Dialog open={index !== null} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ color: COLORS.PRIMARY, fontWeight: 'bold' }}>{title}</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 1 }}>
					{fields.map((field, i) => {
						const isLast = i === fields.length - 1;
						return (
							<TextField
								key={field.label}
								inputRef={(element: HTMLInputElement | null) => {
									inputRefs.current[i] = element;
								}}
								label={field.label}
								value={values[i] ?? ''}
								onChange={(event) => handleChange(i, event.target.value)}
								onKeyDown={handleKeyDown(i)}
								error={!fieldIsValid[i]}
								helperText={
									fieldIsValid[i]
										? isLast
											? 'Press Enter to save and go to the next row'
											: 'Press Enter to go to the next field'
										: field.invalidText
								}
								inputProps={{ inputMode: 'decimal' }}
								autoFocus={i === 0}
								fullWidth
							/>
						);
					})}
				</Box>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
				<Button variant="outlined" onClick={onClose}>
					Cancel
				</Button>

				<Box sx={{ display: 'flex', gap: 1 }}>
					<Button variant="outlined" onClick={goPrevious} disabled={!hasPrevious}>
						<ArrowBack sx={{ mr: 1 }} />
						Save and Go to the Previous
					</Button>
					<Button variant="outlined" onClick={handleButtonSave}>
						Save and Close
					</Button>

					<Button variant="contained" onClick={() => hasNext && goNext()} disabled={!hasNext}>
						Save and Go to the Next <ArrowForward sx={{ ml: 1 }} />
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};
